import React, { useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Scatter } from "react-chartjs-2";

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);
const bvimage = "/assets/biasvariance.png";

const bvLibrary = {
  Manufacturing: {
    Production: [
      "Production Output Variation",
      "Machine Setup Variation",
      "Production Cycle Time",
      "Batch Processing Time",
      "Assembly Line Balance",
    ],

    Quality: [
      "Quality Defects",
      "Inspection Failure Rate",
      "Product Return Rate",
      "Warranty Claim Rate",
    ],

    Procurement: [
      "Raw Material Quality Variation",
      "Supplier Delivery Delay",
      "Procurement Price Variation",
      "Vendor Reliability",
    ],

    Maintenance: [
      "Machine Downtime",
      "Preventive Maintenance Delay",
      "Tool Calibration Variation",
      "Machine Maintenance Compliance",
    ],

    Operations: [
      "Energy Consumption Variation",
      "Electricity Cost Fluctuation",
      "Water Usage Variation",
      "Packaging Cost Variation",
    ],
  },

  Retail: {
    Sales: [
      "Retail Pricing Changes",
      "Sales Conversion Rate",
      "Marketing Campaign Performance",
      "Lead Response Time",
    ],

    Inventory: [
      "Inventory Stockouts",
      "Warehouse Space Utilization",
      "Inventory Shrinkage",
      "Cold Storage Temperature Variation",
    ],

    Customer: [
      "Customer Complaints",
      "Customer Retention",
      "Brand Reputation Feedback",
      "Social Media Engagement",
    ],
  },

  Agriculture: {
    Crop: [
      "Crop Yield Variation",
      "Seed Quality Variation",
      "Fertilizer Application Variation",
      "Irrigation Timing Variation",
    ],

    Market: [
      "Market Demand Fluctuation",
      "Distributor Sales Variation",
      "Channel Inventory Variation",
    ],

    Risk: ["Weather Risk Impact", "Transport Cost Variation"],
  },

  Healthcare: {
    Operations: [
      "Clinic Patient Waiting Time",
      "Diagnosis Consistency",
      "Treatment Protocol Adherence",
    ],

    Pharmacy: ["Pharmacy Stock Availability", "Lab Test Turnaround Time"],
  },

  Education: {
    Students: [
      "Student Performance Variation",
      "Homework Completion Rates",
      "Course Completion Rate",
    ],

    Teaching: ["Teacher Evaluation Consistency", "Training Effectiveness"],
  },

  HORECA: {
    Kitchen: [
      "Restaurant Food Quality Variation",
      "Recipe Consistency",
      "Kitchen Preparation Time",
    ],

    Service: [
      "Service Waiting Time",
      "Table Turnover Rate",
      "Customer Feedback Variation",
    ],
  },
};

const BVUseCases = () => {
  const [sector, setSector] = useState("");
  const [func, setFunc] = useState("");
  const [selectedCase, setSelectedCase] = useState("");

  const [date, setDate] = useState("");

  const [expectedBias, setExpectedBias] = useState("");
  const [expectedVariance, setExpectedVariance] = useState("");

  const [actualBias, setActualBias] = useState("");
  const [actualVariance, setActualVariance] = useState("");

  const [remarks, setRemarks] = useState("");

  const [showExample,setShowExample]=useState(false);
  const [showGuide,setShowGuide]=useState(false);
  
  const exampleData = {
    useCase: "Production Output Variation",
    expectedBias: 2,
    expectedVariance: 3,
    actualBias: 6,
    actualVariance: 8,
    remarks: "Machine setup inconsistent between shifts causing output variation."
  };

  const howToUseSteps = [
    "1️⃣ Select your sector, function, and operational use case.",
    "2️⃣ Record the expected bias and variance for the process (your target).",
    "3️⃣ Enter the actual bias and variance observed today.",
    "4️⃣ The tool calculates severity of variation.",
    "5️⃣ Read the recommendation to stabilize your process.",
    "6️⃣ Save entries daily to track improvement trends."
  ];

  const [showAnalysis, setShowAnalysis] = useState(false);

  const bias = Number(actualBias) || 0;
  const variance = Number(actualVariance) || 0;

  const chartData = {
    datasets: [
      {
        label: "Your Process Position",
        data: [{ x: variance, y: bias }],
        backgroundColor: "yellow",
        pointRadius: 10,
      },
    ],
  };

  const chartOptions = {
    scales: {
      x: {
        min: 0,
        max: 10,
        title: {
          display: true,
          text: "Variance",
          color: "white",
        },
          ticks: {
          color: "white",        
        },
        grid: {
          color: "#444",
        },
      },
      y: {
        min: 0,
        max: 10,
        title: {
          display: true,
          text: "Bias",
          color: "white",
        },
          ticks: {
          color: "white",        
        },
        grid: {
          color: "#444",
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: "white",
        },
      },
    },
  };

//   const getQuadrant = () => {
//     if (!actualBias || !actualVariance) return "";

//     const bias = Number(actualBias);
//     const variance = Number(actualVariance);

//     if (bias <= 5 && variance <= 5) return "Low Bias / Low Variance (Excellent)";
//     if (bias > 5 && variance <= 5) return "High Bias / Low Variance (Rigid Process)";
//     if (bias <= 5 && variance > 5) return "Low Bias / High Variance (Chaotic Process)";
//     return "High Bias / High Variance (Danger Zone)";
//   };

  const calculateSeverity = () => {
    if (!expectedVariance || !actualVariance) return null;

    const gap = actualVariance - expectedVariance;

    if (gap <= 2) return "Low";
    if (gap <= 5) return "Medium";
    if (gap <= 10) return "High";

    return "Critical";
  };

  const severity = calculateSeverity();


  const getRecommendation = () => {
    if (!actualBias || !actualVariance) return "";

    if (actualBias > 7 && actualVariance > 7)
      return "Danger Zone: Run 5 Whys + Process Standardization + Control Charts.";

    if (actualBias > 7)
      return "High Bias: Experiment with new approaches and collect feedback.";

    if (actualVariance > 7)
      return "High Variance: Standardize process using SOP + Kanban.";

    return "Balanced: Continue monitoring.";
  };

  const recommendation = getRecommendation();

  const handleSave = () => {
    const payload = {
      sector,
      function: func,
      useCase: selectedCase,
      date,
      expectedBias,
      expectedVariance,
      actualBias,
      actualVariance,
      remarks,
    };

    // console.log("Save to DB:", payload);
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <h1 className="text-2xl font-bold text-yellow-200 mb-4">
        Bias-Variance Toolkit
      </h1>
      <p className="text-gray-300 mb-6">
        Track operational variation in your MSME and quickly understand whether
        your process is stable or risky.
      </p>

      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setShowExample(!showExample)}
          className="bg-blue-700 px-4 py-2 rounded text-sm"
        >
          Show Example
        </button>

        <button
          onClick={() => setShowGuide(!showGuide)}
          className="bg-purple-700 px-4 py-2 rounded text-sm"
        >
          How to Use
        </button>
      </div>

      {showExample && (
        <div className="bg-gray-900 border border-gray-700 rounded p-4 mb-6">
          <h3 className="text-yellow-300 mb-2">
            Example: Manufacturing Scenario
          </h3>

          <ul className="text-sm space-y-1 [&_li]:text-gray-300">
            <li>
              <b>Use Case:</b> {exampleData.useCase}
            </li>
            <li>
              <b>Expected Bias:</b> {exampleData.expectedBias}
            </li>
            <li>
              <b>Expected Variance:</b> {exampleData.expectedVariance}
            </li>
            <li>
              <b>Actual Bias:</b> {exampleData.actualBias}
            </li>
            <li>
              <b>Actual Variance:</b> {exampleData.actualVariance}
            </li>
            <li>
              <b>Observation:</b> {exampleData.remarks}
            </li>
          </ul>
        </div>
      )}

      {showGuide && (
        <div className="bg-gray-900 border border-gray-700 rounded p-4 mb-6">
          <h3 className="text-yellow-300 mb-2">
            How MSME Owners Should Use This
          </h3>

          <ul className="text-sm space-y-2 [&_li]:text-gray-300">
            {howToUseSteps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ul>
        </div>
      )}

      <h2 className="text-yellow-300 mb-2">Select Sector</h2>
      <select
        className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2 mb-4"
        value={sector}
        onChange={(e) => {
          setSector(e.target.value);
          setFunc("");
          setSelectedCase("");
        }}
      >
        <option value="">Select Sector</option>
        {Object.keys(bvLibrary).map((s, i) => (
          <option key={i}>{s}</option>
        ))}
      </select>

      {sector && (
        <>
          <h2 className="text-yellow-300 mb-2">Select Function</h2>
          <select
            className="w-56 h-12 bg-gray-900 border border-gray-700 rounded p-2 mb-4"
            value={func}
            onChange={(e) => {
              setFunc(e.target.value);
              setSelectedCase("");
            }}
          >
            <option value="">Select Function</option>
            {Object.keys(bvLibrary[sector]).map((f, i) => (
              <option key={i}>{f}</option>
            ))}
          </select>
        </>
      )}

      {func && (
        <>
          <h2 className="text-yellow-300 mb-2">Select Use Case</h2>
          <select
            className="w-72 h-12 bg-gray-900 border border-gray-700 rounded p-2 mb-6"
            value={selectedCase}
            onChange={(e) => setSelectedCase(e.target.value)}
          >
            <option value="">Select Use Case</option>
            {bvLibrary[sector][func].map((u, i) => (
              <option key={i}>{u}</option>
            ))}
          </select>
        </>
      )}
      {selectedCase && (
        <div className="space-y-4">
          <div>
            <h2 className="text-yellow-300 mb-1">Date</h2>
            <input
              type="date"
              className="w-40 bg-gray-900 border border-gray-700 p-3 rounded"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <h2 className="text-yellow-300 text-sm">Expected Bias</h2>
              <input
                type="number"
                className="w-32 bg-gray-900 border border-gray-700 p-2 rounded"
                value={expectedBias}
                onChange={(e) => setExpectedBias(e.target.value)}
              />
            </div>

            <div>
              <h2 className="text-yellow-300 text-sm">Actual Bias</h2>
              <input
                type="number"
                className="w-32 bg-gray-900 border border-gray-700 p-2 rounded"
                value={actualBias}
                onChange={(e) => setActualBias(e.target.value)}
              />
            </div>
            <div>
              <h2 className="text-yellow-300 text-sm">Expected Variance</h2>
              <input
                type="number"
                className="w-32 bg-gray-900 border border-gray-700 p-2 rounded"
                value={expectedVariance}
                onChange={(e) => setExpectedVariance(e.target.value)}
              />
            </div>
            <div>
              <h2 className="text-yellow-300 text-sm">Actual Variance</h2>

              <input
                type="number"
                className="w-32 bg-gray-900 border border-gray-700 p-2 rounded"
                value={actualVariance}
                onChange={(e) => setActualVariance(e.target.value)}
              />
            </div>
          </div>
          <div>
            <h2 className="text-yellow-300">Remarks</h2>
            <textarea
              style={{ height: "200px", width: "100%" }}
              className="bg-gray-900 border text-white border-gray-700 p-3 rounded"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          {severity && (
            <div className="bg-gray-900 border border-gray-700 rounded p-3">
              <p className="text-sm text-cyan-300">
                Variance Severity:
                <span className="font-bold ml-2">{severity}</span>
              </p>
            </div>
          )}

          {recommendation && (
            <div className="bg-gray-900 border border-gray-700 rounded p-3">
              <p className="text-green-300 text-sm">{recommendation}</p>
            </div>
          )}

          <div className="mt-3">
            <button
              onClick={() => setShowAnalysis(!showAnalysis)}
              className="text-xs lg:text-sm w-40 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
            >
              Show Analysis
            </button>
          </div>

          {showAnalysis && (
            <div className="w-1/2 mt-6 bg-gray-900 border border-gray-700 rounded p-4">
              <h3 className="text-yellow-300 mb-3">
                Dynamic Bias-Variance Infographic
              </h3>

              <div className="bg-black p-3 rounded">
                <Scatter data={chartData} options={chartOptions} />
              </div>

              <p className="text-sm text-gray-300 mt-4">
                Your operational process is plotted on the Bias-Variance map. Lower-left zone means stable processes, while upper-right zone
                indicates high risk processes needing immediate improvement.
              </p>
            </div>
          )}

          <div className="flex flex-col items-center justify-center border border-cyan-200 mt-2 max-w-6xl p-2">
            <h2 className="text-yellow-300 mb-2">Bias-Variance Target Board</h2>
            <div className="flex-shrink-0 w-80 h-full rounded overflow-hidden bg-gray-700 mb-4">
              <img
                src={bvimage}
                alt="Bias-Variance Image"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 text-center text-xs lg:text-sm">
              <div className="bg-green-800 p-3 rounded">
                Low Bias
                <br />
                Low Variance
                <br />
                <span className="text-green-300">Excellent</span>
              </div>
              <div className="bg-blue-800 p-3 rounded">
                High Bias
                <br />
                Low Variance
                <br />
                <span className="text-blue-300">Rigid</span>
              </div>

              <div className="bg-yellow-700 p-3 rounded">
                Low Bias
                <br />
                High Variance
                <br />
                <span className="text-yellow-200">Chaotic</span>
              </div>

              <div className="bg-red-800 p-3 rounded">
                High Bias
                <br />
                High Variance
                <br />
                <span className="text-red-300">Danger</span>
              </div>
            </div>
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            className="text-xs lg:text-sm w-48 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 rounded mt-4"
          >
            Save to Database
          </button>
        </div>
      )}
    </div>
  );
};

export default BVUseCases;